<h2 mat-dialog-title class="p-3">
  {{ mode === 'edit' ? 'Edit Data Table Block' : (mode === 'preview' ? 'Preview Data Table' : 'Edit Data Table Block')
  }}
</h2>

<form [formGroup]="form" (ngSubmit)="save()">
  <mat-dialog-content #dialogContent class="dialog-content">

    <!-- FORM FIELDS -->
    <mat-form-field appearance="fill" class="full-width">
      <mat-label>Placeholder Name</mat-label>
      <input matInput formControlName="placeholderName" />
    </mat-form-field>

    <mat-form-field appearance="fill" class="full-width">
      <mat-label>Display Title</mat-label>
      <input matInput formControlName="displayTitle" />
      <mat-error *ngIf="form.controls['displayTitle'].hasError('required') && form.controls['displayTitle'].touched">
        Display Title is required
      </mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>SQL Query</mat-label>
      <textarea matInput formControlName="sqlQuery" rows="6"></textarea>
      <mat-error *ngIf="form.controls['sqlQuery'].hasError('required') && form.controls['sqlQuery'].touched">
        SQL Query is required
      </mat-error>
    </mat-form-field>

    <mat-form-field appearance="fill" class="full-width">
      <mat-label>Secret Key</mat-label>
      <input matInput formControlName="secretKey" />
    </mat-form-field>

    <!-- TEST SQL -->
    <div style="margin-top:12px; display:flex; gap:12px; align-items:center;">
      <button mat-stroked-button type="button" (click)="testSql()" [disabled]="testing">
        {{ testing ? 'Testing...' : 'Test SQL' }}
      </button>
      <span style="font-size:13px;">
        <ng-container *ngIf="sqlTested">SQL tested ✓</ng-container>
        <ng-container *ngIf="!sqlTested && !testing">Not tested</ng-container>
      </span>
    </div>

    <!-- PREVIEW TABLE -->
    <div *ngIf="preview" class="preview-box" style="margin-top:12px;">
      <h3>Preview (from Test SQL)</h3>

      <table class="preview-table" style="width:100%; border-collapse:collapse;" border="1">
        <thead>
          <tr>
            <th *ngFor="let c of preview.cols" [style.background]="headerBgColor" [style.color]="headerTextColor"
              [style.font-family]="headerFontFamily" [style.font-size]="headerFontSizeString"
              [style.font-weight]="headerFontWeight" [style.font-style]="headerFontStyle"
              [style.text-align]="headerTextAlign" [style.text-decoration]="headerTextDecoration"
              style="padding:6px; text-align:left;">
              {{ c.header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let p of preview.products">
            <td *ngFor="let c of preview.cols" style="padding:6px; border-top:1px solid #ddd;"
              [style.background]="bodyBgColor" [style.color]="bodyTextColor" [style.font-family]="bodyFontFamily"
              [style.font-size]="bodyFontSizeString" [style.font-weight]="bodyFontWeight"
              [style.font-style]="bodyFontStyle" [style.text-align]="bodyTextAlign"
              [style.text-decoration]="bodyTextDecoration">
              {{ p[c.field] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- STYLE TARGET TOGGLE + TOOLBAR -->
    <div *ngIf="preview" class="mt-3">
      <!-- Header / Body toggle -->
      <div class="mb-2">
        <button type="button" class="btn btn-sm me-1" [class.btn-primary]="activeStyleTarget === 'header'"
          [class.btn-outline-secondary]="activeStyleTarget !== 'header'" (click)="setActiveTarget('header')">
          Header
        </button>

        <button type="button" class="btn btn-sm" [class.btn-primary]="activeStyleTarget === 'body'"
          [class.btn-outline-secondary]="activeStyleTarget !== 'body'" (click)="setActiveTarget('body')">
          Body
        </button>
      </div>

      <!-- Toolbar -->
      <div class="email-template-toolbar">
        <!-- Heading selector -->
        <div class="email-template-tool-group" matTooltipPosition="above" matTooltip="Font size">
          <select class="email-template-heading-selector" [(ngModel)]="activeHeading"
            [ngModelOptions]="{standalone: true}">
            <option value="p">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
          </select>
        </div>

        <div class="email-template-divider"></div>

        <!-- Bold / Italic / Underline / Strikethrough -->
        <div class="email-template-tool-group">
          <button type="button" matTooltipPosition="above" matTooltip="Bold" class="border"
            [class.active]="isBoldActive()" (click)="toggleBold()" title="Bold">
            <span class="material-icons">format_bold</span>
          </button>
          <button type="button" matTooltipPosition="above" matTooltip="Italic" class="border"
            [class.active]="isItalicActive()" (click)="toggleItalic()" title="Italic">
            <span class="material-icons">format_italic</span>
          </button>
          <button type="button" matTooltipPosition="above" matTooltip="Underline" class="border"
            [class.active]="isUnderlineActive()" (click)="toggleUnderline()" title="Underline">
            <span class="material-icons">format_underlined</span>
          </button>
          <button type="button" matTooltipPosition="above" matTooltip="Strikethrough" class="border"
            [class.active]="isStrikeActive()" (click)="toggleStrike()" title="Strikethrough">
            <span class="material-icons">strikethrough_s</span>
          </button>
        </div>

        <div class="email-template-divider"></div>

        <!-- Alignment -->
        <div class="email-template-tool-group">
          <button type="button" matTooltipPosition="above" matTooltip="Align Left" class="border"
            [class.active]="isAlignActive('left')" (click)="setAlign('left')" title="Align Left">
            <span class="material-icons">format_align_left</span>
          </button>
          <button type="button" matTooltipPosition="above" matTooltip="Align Center" class="border"
            [class.active]="isAlignActive('center')" (click)="setAlign('center')" title="Align Center">
            <span class="material-icons">format_align_center</span>
          </button>
          <button type="button" matTooltipPosition="above" matTooltip="Align Right" class="border"
            [class.active]="isAlignActive('right')" (click)="setAlign('right')" title="Align Right">
            <span class="material-icons">format_align_right</span>
          </button>
        </div>

        <div class="email-template-divider"></div>

        <!-- Color Pickers -->
        <div class="email-template-tool-group email-template-color-group">
          <div class="email-template-color-wrapper border" title="Text Color" matTooltipPosition="above"
            matTooltip="Font color">
            <span class="material-icons email-template-color-icon" [style.color]="activeTextColor">
              format_color_text
            </span>
            <input type="color" [value]="activeTextColor" (change)="onTextColorChange($event)" />
          </div>

          <div class="email-template-color-wrapper border" title="Background Color" matTooltipPosition="above"
            matTooltip="Background color">
            <span class="material-icons email-template-color-icon" [style.color]="activeBgColor">
              border_color
            </span>
            <input type="color" [value]="activeBgColor" (change)="onBgColorChange($event)" />
          </div>
        </div>
      </div>
    </div>

  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button class="scraii-secondary-btn" type="button" (click)="cancel()">Cancel</button>
    <button class="scraii-save-btn ms-2" type="submit" [disabled]="form.invalid || !sqlTested">
      Save
    </button>
  </mat-dialog-actions>
</form>